import React from 'react';
import { useQuery } from '@apollo/client';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Alert,
  Box,
  Stack,
  Divider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import Load from '../Load';
import {
  LIGA_CONTA_IUGU_DOCUMENTOS_POR_LIGA_ID,
  LigaContaIuguDocumentosPorLigaIdData,
  LigaContaIuguDocumentosPorLigaIdVars,
} from '../../services/ligaContaIuguService';

interface ConfigPagamentosDocumentacaoTabProps {
  ligaId: string;
}

const ConfigPagamentosDocumentacaoTab: React.FC<ConfigPagamentosDocumentacaoTabProps> = ({ ligaId }) => {
  const { data, loading, error } = useQuery<
    LigaContaIuguDocumentosPorLigaIdData,
    LigaContaIuguDocumentosPorLigaIdVars
  >(LIGA_CONTA_IUGU_DOCUMENTOS_POR_LIGA_ID, {
    variables: { ligaId },
    skip: !ligaId,
  });

  const handleDownload = (url: string | null | undefined) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const documentos = data?.ligaContaIuguDocumentosPorLigaId;

  if (loading) {
    return <Load />;
  }

  if (error) {
    return (
      <Alert severity="error">
        Erro ao carregar documentação. Tente novamente.
      </Alert>
    );
  }

  const FieldDisplay: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== ''
      ? String(value)
      : 'Não informado';
    const isEmptyValue = displayValue === 'Não informado';

    return (
      <Box>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          {label}
        </Typography>
        <Typography
          variant="body1"
          color={isEmptyValue ? 'text.secondary' : 'text.primary'}
          sx={{ fontStyle: isEmptyValue ? 'italic' : 'normal' }}
        >
          {displayValue}
        </Typography>
      </Box>
    );
  };

  return (
    <Card variant="outlined">
      <CardHeader title="Documentação da Conta IUGU" />
      <CardContent>
        {!documentos && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Nenhuma documentação cadastrada para esta liga. Os campos abaixo estão vazios.
          </Alert>
        )}
        <Stack spacing={3}>
          {/* Informações Pessoais/Empresa */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Informações Gerais
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mt: 2 }}>
              <FieldDisplay
                label="Tipo de Pessoa"
                value={documentos?.tipoPessoa === 'PESSOA_FISICA' ? 'Pessoa Física' : documentos?.tipoPessoa === 'PESSOA_JURIDICA' ? 'Pessoa Jurídica' : null}
              />
              <FieldDisplay label="CPF" value={documentos?.cpf} />
              <FieldDisplay label="CNPJ" value={documentos?.cnpj} />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mt: 2 }}>
              <FieldDisplay label="Nome" value={documentos?.nome} />
              <FieldDisplay label="Nome da Empresa" value={documentos?.nomeEmpresa} />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mt: 2 }}>
              <FieldDisplay label="Nome do Responsável" value={documentos?.nomeResp} />
              <FieldDisplay label="CPF do Responsável" value={documentos?.respCpf} />
            </Stack>

            <Box sx={{ mt: 2 }}>
              <FieldDisplay label="Telefone" value={documentos?.telefone} />
            </Box>
          </Box>

          <Divider />

          {/* Dados Bancários */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Dados Bancários
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mt: 2 }}>
              <FieldDisplay label="Banco" value={documentos?.banco} />
              <FieldDisplay label="Agência" value={documentos?.bancoAg} />
              <FieldDisplay label="Conta Corrente" value={documentos?.bancoCc} />
            </Stack>
            <Box sx={{ mt: 2 }}>
              <FieldDisplay
                label="Tipo de Conta"
                value={documentos?.tipoConta === 'CORRENTE' ? 'Conta Corrente' : documentos?.tipoConta === 'POUPANCA' ? 'Poupança' : null}
              />
            </Box>
          </Box>

          <Divider />

          {/* Configurações de Negócio */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Configurações de Negócio
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mt: 2 }}>
              <FieldDisplay label="Faixa de Preço" value={documentos?.priceRange} />
              <FieldDisplay label="Tipo de Negócio" value={documentos?.businessType} />
              <FieldDisplay label="Receita Estimada" value={documentos?.estimatedRevenue} />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mt: 2 }}>
              <FieldDisplay
                label="Produtos Físicos"
                value={documentos?.physicalProducts !== null && documentos?.physicalProducts !== undefined
                  ? (documentos.physicalProducts ? 'Sim' : 'Não')
                  : null
                }
              />
              <FieldDisplay
                label="Transferência Automática"
                value={documentos?.automaticTransfer !== null && documentos?.automaticTransfer !== undefined
                  ? (documentos.automaticTransfer ? 'Sim' : 'Não')
                  : null
                }
              />
            </Stack>
          </Box>

          <Divider />

          {/* Documentos Anexados */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Documentos Anexados
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Arquivo de Identificação
                </Typography>
                {documentos?.arquivoIdUrl ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(documentos.arquivoIdUrl)}
                  >
                    Download
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Arquivo não enviado
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Selfie
                </Typography>
                {documentos?.selfieUrl ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(documentos.selfieUrl)}
                  >
                    Download
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Arquivo não enviado
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Comprovante de Endereço
                </Typography>
                {documentos?.comprovanteEnderecoUrl ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(documentos.comprovanteEnderecoUrl)}
                  >
                    Download
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Arquivo não enviado
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Relatório Financeiro
                </Typography>
                {documentos?.relatorioFinanceiroUrl ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(documentos.relatorioFinanceiroUrl)}
                  >
                    Download
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Arquivo não enviado
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Contrato Social
                </Typography>
                {documentos?.contratoSocialUrl ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(documentos.contratoSocialUrl)}
                  >
                    Download
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Arquivo não enviado
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ConfigPagamentosDocumentacaoTab;
